import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {stateApi} from './utils/stateApi'
import {Heartbeat, monitorList, StatusPage, StatusPageHeartbeat} from './utils/stateApiType'
import {FIP101_STAKE_STATE_NAME} from './utils/constants'
import serverData from './data/config.json'
import {fip101StatusMessages as text} from './data/fip101StatusMessages'

const REFRESH_INTERVAL_MS = 6 * 60 * 1000
const STALE_AFTER_MS = 15 * 60 * 1000
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000
const STAKING_SITE_URL = 'https://index-mining-beta.fractalbitcoin.io/'

type DisplayStatus = 'operational' | 'degraded' | 'maintenance' | 'unavailable'

type MonitorConfig = {
    key: string
    title: string
    name: string
}

type StatusSlot = {
    at: Date
    status: DisplayStatus
}

type TimelineSlot = StatusSlot | null

type ServiceState = {
    key: string
    title: string
    status: DisplayStatus
    uptime: number | null
    latestAt: Date | null
    slots: TimelineSlot[]
}

type StatusData = {
    overall: DisplayStatus
    services: ServiceState[]
    lastUpdated: Date | null
}

type StatusApi = Pick<typeof stateApi, 'getStatusPage' | 'getStatusPageHeartbeatWithServerTime'>

const monitorConfigs = serverData.fip101Stake.monitors as MonitorConfig[]

function parseUtc(value: string): Date | null {
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
        ? `${value.replace(' ', 'T')}Z`
        : value
    const date = new Date(normalized)
    return Number.isNaN(date.getTime()) ? null : date
}

function statusFromCode(status: number): DisplayStatus {
    if (status === 1) return 'operational'
    if (status === 0) return 'degraded'
    if (status === 2 || status === 3) return 'maintenance'
    return 'unavailable'
}

function statusLabel(status: DisplayStatus): string {
    if (status === 'operational') return text.systemOperational
    if (status === 'degraded') return text.degraded
    if (status === 'maintenance') return text.maintenance
    return text.unavailable
}

function statusColor(status: DisplayStatus): string {
    if (status === 'operational') return 'bg-[#3ac79c]'
    if (status === 'degraded') return 'bg-[#f55454]'
    if (status === 'maintenance') return 'bg-[#eeb826]'
    return 'bg-[#98a2b3]'
}

function emptyService(config: MonitorConfig): ServiceState {
    return {
        key: config.key,
        title: config.title,
        status: 'unavailable',
        uptime: null,
        latestAt: null,
        slots: [],
    }
}

function buildService(
    config: MonitorConfig,
    monitors: monitorList[],
    heartbeatData: StatusPageHeartbeat,
    now: Date | null,
): ServiceState {
    const matched = monitors.filter((monitor) => monitor.name === config.name)
    if (matched.length !== 1) return emptyService(config)

    const heartbeats: Heartbeat[] = heartbeatData.heartbeatList[String(matched[0].id)] || []
    const heartbeatsWithTime = heartbeats
        .map((heartbeat) => {
            const at = parseUtc(heartbeat.time)
            return at ? {at, status: statusFromCode(heartbeat.status)} : null
        })
        .filter((slot): slot is StatusSlot => slot !== null)
        .sort((left, right) => left.at.getTime() - right.at.getTime())
        .slice(-72)

    const latest = heartbeatsWithTime[heartbeatsWithTime.length - 1]
    if (!latest) return emptyService(config)

    const uptime = (heartbeatsWithTime.filter((slot) => slot.status === 'operational').length / heartbeatsWithTime.length) * 100
    const stale = !now || now.getTime() - latest.at.getTime() > STALE_AFTER_MS
    const slots: TimelineSlot[] = Array.from({length: 72}, (_, index) => {
        const heartbeatIndex = heartbeatsWithTime.length - 72 + index
        return heartbeatIndex >= 0 ? heartbeatsWithTime[heartbeatIndex] : null
    })

    return {
        key: config.key,
        title: config.title,
        status: stale ? 'unavailable' : latest.status,
        uptime,
        latestAt: latest.at,
        slots,
    }
}

function buildStatusData(page: StatusPage, heartbeatData: StatusPageHeartbeat, serverTime: Date | null): StatusData {
    const monitors = page.publicGroupList.flatMap((group) => group.monitorList)
    const services = monitorConfigs.map((config) => buildService(config, monitors, heartbeatData, serverTime))
    const statuses = services.map((service) => service.status)
    const overall: DisplayStatus = statuses.includes('unavailable')
        ? 'unavailable'
        : statuses.includes('degraded')
            ? 'degraded'
            : statuses.includes('maintenance')
                ? 'maintenance'
                : 'operational'
    const timestamps = services.map((service) => service.latestAt?.getTime())

    return {
        overall,
        services,
        lastUpdated: timestamps.every((timestamp) => typeof timestamp === 'number')
            ? new Date(Math.min(...(timestamps as number[])))
            : null,
    }
}

function formatDate(date: Date | null): string {
    if (!date) return '--'
    return new Intl.DateTimeFormat('en', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).format(date)
}

function formatClock(date: Date): string {
    return new Intl.DateTimeFormat('en', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date)
}

function ServiceRow({service}: { service: ServiceState }) {
    const axisIndexes = useMemo(() => {
        if (!service.latestAt) return []
        return [0, 12, 24, 36, 48, 60, service.slots.length - 1]
    }, [service.latestAt, service.slots.length])

    return <div className="overflow-x-auto px-6 py-6 sm:overflow-visible">
        <div className="min-w-[640px]">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-[18px] font-semibold leading-4 text-black">{service.title}</h2>
                <span className="rounded-[6px] bg-[#3ac79c]/20 px-3 py-1.5 text-[14px] font-bold leading-4 text-[#218c6d] ring-1 ring-[#3ac79c]/20">
                    {service.uptime === null ? '--' : `${service.uptime.toFixed(2)}%`}
                </span>
            </div>
            {axisIndexes.length ? <>
                <div className="mt-6 flex items-stretch gap-[3px]">
                    {service.slots.map((slot, index) => slot ? <span
                        key={`${slot.at.getTime()}-${index}`}
                        tabIndex={0}
                        aria-label={`${formatDate(slot.at)}, ${statusLabel(slot.status)}`}
                        className={`group relative h-[34px] min-w-[4px] flex-1 outline-none focus-visible:ring-2 focus-visible:ring-[#101828] ${statusColor(slot.status)}`}
                    >
                        <span className="pointer-events-none absolute left-1/2 top-[calc(100%+12px)] z-20 hidden min-w-[190px] -translate-x-1/2 rounded-[6px] bg-white px-3 py-2 text-left shadow-[0_4px_12px_rgba(0,0,0,0.18)] group-hover:block group-focus:block">
                            <span aria-hidden="true" className="absolute bottom-full left-1/2 -translate-x-1/2 border-x-[6px] border-b-[7px] border-x-transparent border-b-white"/>
                            <span className="block whitespace-nowrap text-[12px] font-medium leading-5 text-black">{formatDate(slot.at)}</span>
                            <span className={`block text-[12px] font-medium leading-5 ${slot.status === 'degraded' ? 'text-[#f55454]' : 'text-[#3ac79c]'}`}>{statusLabel(slot.status)}</span>
                        </span>
                    </span> : <span key={`empty-${index}`} aria-label={text.noHeartbeatData} className="h-[34px] min-w-[4px] flex-1 bg-black/[0.06]"/>)}
                </div>
                <div className="relative mt-3 h-4 text-[12px] font-medium leading-4 text-black/45 sm:text-[14px]">
                    {axisIndexes.map((index) => {
                        const isFirst = index === 0
                        const isLast = index === service.slots.length - 1
                        return <span
                            key={index}
                            className={`absolute top-0 ${isFirst ? 'left-0' : isLast ? 'right-0' : '-translate-x-1/2'}`}
                            style={isFirst || isLast ? undefined : {left: `${(index / (service.slots.length - 1)) * 100}%`}}
                        >
                            {isLast ? text.now : service.latestAt
                                ? formatClock(new Date(service.latestAt.getTime() - (service.slots.length - 1 - index) * HEARTBEAT_INTERVAL_MS))
                                : '--'}
                        </span>
                    })}
                </div>
            </> : <div className="mt-6 flex h-[34px] items-center justify-center bg-black/[0.05] text-[13px] text-black/55">{text.noHeartbeatData}</div>}
        </div>
    </div>
}

function LoadingSkeleton() {
    return <>
        <section data-testid="fip101-loading-skeleton" className="mt-10 flex w-full flex-col items-center sm:mt-[42px]">
            <div className="h-16 w-full max-w-[480px] animate-pulse rounded-[12px] bg-black/[0.12] sm:h-20"/>
            <div className="mt-4 flex w-full max-w-[400px] animate-pulse items-center justify-center gap-3">
                <span className="h-5 w-[170px] rounded-[4px] bg-black/[0.08]"/>
                <span className="h-5 w-[90px] rounded-[4px] bg-black/[0.08]"/>
            </div>
            <div className="mt-5 flex animate-pulse items-center gap-8">
                <span className="h-4 w-24 rounded-[4px] bg-black/[0.08]"/>
                <span className="h-4 w-24 rounded-[4px] bg-black/[0.08]"/>
            </div>
        </section>
        <section className="mt-11 w-full sm:mt-[46px]">
            <div className="divide-y divide-black/10 rounded-[16px] bg-white ring-2 ring-black/15">
                {monitorConfigs.map((config) => <div key={config.key} className="animate-pulse overflow-x-auto px-6 py-6 sm:overflow-visible">
                    <div className="min-w-[640px]">
                        <div className="flex items-center justify-between gap-4">
                            <span className="h-5 w-40 rounded-[4px] bg-black/[0.08]"/>
                            <span className="h-7 w-[72px] rounded-[6px] bg-black/[0.08]"/>
                        </div>
                        <div className="mt-6 flex h-[34px] gap-[3px]">
                            {Array.from({length: 72}, (_, index) => <span key={index} className="min-w-[4px] flex-1 bg-black/[0.06]"/>)}
                        </div>
                        <div className="mt-3 flex justify-between">
                            <span className="h-4 w-10 rounded-[4px] bg-black/[0.08]"/>
                            <span className="h-4 w-10 rounded-[4px] bg-black/[0.08]"/>
                        </div>
                    </div>
                </div>)}
            </div>
        </section>
    </>
}

export function Fip101Stake({api = stateApi}: { api?: StatusApi }) {
    const [data, setData] = useState<StatusData | null>(null)
    const [loading, setLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [nextRefreshAt, setNextRefreshAt] = useState(() => Date.now() + REFRESH_INTERVAL_MS)
    const [clock, setClock] = useState(() => Date.now())
    const hasDataRef = useRef(false)

    const refresh = useCallback(async () => {
        setNextRefreshAt(Date.now() + REFRESH_INTERVAL_MS)
        setLoading(!hasDataRef.current)
        try {
            const [page, heartbeatData] = await Promise.all([
                api.getStatusPage(FIP101_STAKE_STATE_NAME),
                api.getStatusPageHeartbeatWithServerTime(FIP101_STAKE_STATE_NAME, 72, 5),
            ])
            const nextData = buildStatusData(page, heartbeatData.data, heartbeatData.serverTime)
            hasDataRef.current = true
            setData(nextData)
            setHasError(false)
        } catch {
            setHasError(true)
        } finally {
            setLoading(false)
        }
    }, [api])

    useEffect(() => {
        const previousTitle = document.title
        document.title = 'Fractal Indexer | System Status'
        void refresh()
        const refreshTimer = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS)
        const clockTimer = window.setInterval(() => setClock(Date.now()), 1000)
        return () => {
            document.title = previousTitle
            window.clearInterval(refreshTimer)
            window.clearInterval(clockTimer)
        }
    }, [refresh])

    const overall = hasError ? 'unavailable' : data?.overall || 'unavailable'
    const refreshLeft = Math.max(0, nextRefreshAt - clock)
    const refreshMinutes = Math.floor(refreshLeft / 60000)
    const refreshSeconds = String(Math.floor((refreshLeft % 60000) / 1000)).padStart(2, '0')
    const bannerText = loading && !data
        ? text.checking
        : overall === 'operational'
            ? text.operational
            : overall === 'degraded'
                ? text.degraded
                : overall === 'maintenance'
                    ? text.maintenance
                    : text.unavailable
    const bannerColor = overall === 'operational'
        ? 'bg-[#3ac79c] text-white'
        : overall === 'degraded'
            ? 'bg-[#f55454] text-white'
            : overall === 'maintenance'
                ? 'bg-[#eeb826] text-[#101828]'
                : 'bg-[#667085] text-white'
    const icon = overall === 'operational'
        ? '/fip101/status-check.svg'
        : overall === 'degraded'
            ? '/fip101/status-close.svg'
        : overall === 'maintenance'
            ? '/img/systems-padding.svg'
            : null
    const isInitialLoading = loading && !data

    return <main className="min-h-screen overflow-hidden bg-[#f5eee9] px-5 pb-16 pt-10 font-['DM_Sans',system-ui,sans-serif] text-[#101828] sm:px-8">
        <div className="mx-auto flex w-full max-w-[944px] flex-col items-center">
            <a href={STAKING_SITE_URL} target="_blank" rel="noreferrer" aria-label={text.productName} className="flex items-center gap-[18px] rounded-[16px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f96c04]">
                <img src="/fip101/fractal-logo.svg" alt="" className="h-[56px] w-[56px] rounded-[13px] sm:h-[71px] sm:w-[71px] sm:rounded-[16px]"/>
                <h1 className="text-[26px] font-semibold leading-tight sm:text-[36px]">{text.productName}</h1>
            </a>
            {isInitialLoading ? <LoadingSkeleton/> : <>
            <section className="mt-10 flex w-full flex-col items-center sm:mt-[42px]">
                <div aria-live="polite" className={`flex h-16 w-full max-w-[480px] items-center justify-center gap-3 rounded-[12px] px-6 sm:h-20 ${bannerColor}`}>
                    {icon ? <img src={icon} alt="" className="h-[30px] w-[30px]"/> : null}
                    <p className="text-[20px] font-bold leading-8 sm:text-[24px]">{bannerText}</p>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] font-medium leading-5 text-black/55 sm:text-[14px]">
                    <span>{text.lastUpdated} {formatDate(data?.lastUpdated || null)}</span>
                    <span aria-hidden="true" className="text-black/25">|</span>
                    <span className="inline-flex items-center gap-1.5">
                        <span>{text.refreshIn}</span>
                        <span className="inline-flex min-w-[36px] items-center justify-center rounded-[4px] bg-black/[0.06] px-1.5 py-0.5 text-black/85">{refreshMinutes}m</span>
                        <span className="inline-flex min-w-[40px] items-center justify-center rounded-[4px] bg-black/[0.06] px-1.5 py-0.5 text-black/85">{refreshSeconds}s</span>
                    </span>
                </div>
                <div data-testid="status-legend" className="mt-5 flex items-center gap-8 text-[12px] font-medium text-black/65 sm:text-[14px]">
                    {(['operational', 'degraded'] as DisplayStatus[]).map((status) => <span key={status} className="inline-flex items-center gap-1.5">
                        <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${statusColor(status)}`}/>
                        {statusLabel(status)}
                    </span>)}
                </div>
                {hasError ? <div className="mt-5 flex flex-col items-center gap-2 text-center text-[14px] text-[#475467]">
                    {data ? <p>{text.retainedData}</p> : null}
                    <button type="button" onClick={() => void refresh()} className="rounded-[6px] border border-black/20 bg-white px-4 py-2 font-semibold text-[#101828]">{text.retry}</button>
                </div> : null}
            </section>
            {data ? <section className="mt-11 w-full sm:mt-[46px]">
                <div className="divide-y divide-black/10 rounded-[16px] bg-white ring-2 ring-black/15">
                    {data.services.map((service) => <ServiceRow key={service.key} service={service}/>) }
                </div>
            </section> : null}
            </>}
        </div>
    </main>
}
